import { useEffect, useRef, useState, useCallback } from "react";
import { X, MessageCircle, ArrowRight, Volume2, VolumeX } from "lucide-react";
import { getWhatsAppLink } from "@/lib/contact";
import { Link, useLocation } from "react-router-dom";
import { useLanguage } from "@/lib/i18n";

const CLIPS = [
  {
    src: "/promo/clip-01-port.mp4",
    duration: 7,
    headline: "¿Tu mercancía no avanza\ny nadie te da respuestas?",
    sub: null,
  },
  {
    src: "/promo/clip-02-inspection.mp4",
    duration: 7,
    headline: "NaviCargo conecta tu empresa\ncon el mundo.",
    sub: "Verificamos cada proveedor antes de que compres.",
  },
  {
    src: "/promo/clip-04-ship.mp4",
    duration: 7,
    headline: "Inspeccionamos en origen.\nCoordinamos tu carga.",
    sub: "De México al mundo — o del mundo hacia ti.",
  },
  {
    src: "/promo/clip-05-warehouse.mp4",
    duration: 7,
    headline: "Más control. Menos riesgo.\nOperaciones claras.",
    sub: null,
  },
];

const EN_CLIPS = [
  { headline: "Is your cargo stuck\nwith no answers?", sub: null },
  { headline: "NaviCargo connects your business\nwith the world.", sub: "We verify every supplier before you buy." },
  { headline: "We inspect at origin.\nWe coordinate your cargo.", sub: "From Mexico to the world — or from the world to you." },
  { headline: "More control. Less risk.\nClear operations.", sub: null },
];

const TOTAL_DURATION = CLIPS.reduce((s, c) => s + c.duration, 0); // 28 s

export default function PromoModal() {
  const [open, setOpen] = useState(false);
  const [clipIdx, setClipIdx] = useState(0);
  const [ended, setEnded] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [muted, setMuted] = useState(false);
  const [textVisible, setTextVisible] = useState(true);
  const [audioReady, setAudioReady] = useState(false);
  const [audioNeedsGesture, setAudioNeedsGesture] = useState(false);
  const location = useLocation();
  const { isEnglish, t } = useLanguage();

  const narrationRef = useRef(null);
  const musicRef = useRef(null);
  const videoRef = useRef(null);
  const timerRef = useRef(null);
  const elapsedRef = useRef(0);

  const startAudio = useCallback(() => {
    const players = [narrationRef.current, musicRef.current].filter(Boolean);

    if (!players.length) {
      setAudioReady(true);
      setAudioNeedsGesture(false);
      return Promise.resolve();
    }

    players.forEach((player) => {
      player.muted = muted;
      player.volume = player === narrationRef.current ? 1 : 0.18;
    });

    return Promise.all(players.map((player) => player.play()))
      .then(() => {
        setAudioReady(true);
        setAudioNeedsGesture(false);
      })
      .catch(() => {
        players.forEach((player) => player.pause());
        setAudioReady(false);
        setAudioNeedsGesture(true);
      });
  }, [muted]);

  // Show the preview every time the user enters Inicio.
  useEffect(() => {
    let timer;

    if (location.pathname === "/") {
      timer = setTimeout(() => {
        elapsedRef.current = 0;
        setElapsed(0);
        setClipIdx(0);
        setEnded(false);
        setTextVisible(true);
        setAudioReady(false);
        setAudioNeedsGesture(false);
        setOpen(true);
      }, 900);
    } else {
      clearInterval(timerRef.current);
      setOpen(false);
      setEnded(false);
      elapsedRef.current = 0;
      setElapsed(0);
      narrationRef.current?.pause();
      musicRef.current?.pause();
      if (narrationRef.current) narrationRef.current.currentTime = 0;
      if (musicRef.current) musicRef.current.currentTime = 0;
    }

    return () => clearTimeout(timer);
  }, [location.pathname]);

  // Try autoplay, then retry from the first user gesture if the browser blocks sound.
  useEffect(() => {
    if (!open) return;
    const retryFromGesture = () => {
      void startAudio();
    };

    void startAudio();
    window.addEventListener("pointerdown", retryFromGesture, { once: true, passive: true });
    window.addEventListener("keydown", retryFromGesture, { once: true });

    return () => {
      window.removeEventListener("pointerdown", retryFromGesture);
      window.removeEventListener("keydown", retryFromGesture);
    };
  }, [open, startAudio]);

  // Progress ticker
  useEffect(() => {
    if (!open || ended) return;
    timerRef.current = setInterval(() => {
      elapsedRef.current += 0.1;
      setElapsed(elapsedRef.current);
    }, 100);
    return () => clearInterval(timerRef.current);
  }, [open, ended]);

  // Clip transition logic
  useEffect(() => {
    if (!open) return;
    let offset = 0;
    for (let i = 0; i < CLIPS.length; i++) {
      const clipStart = offset;
      const clipEnd = offset + CLIPS[i].duration;
      if (elapsed >= clipStart && elapsed < clipEnd) {
        if (clipIdx !== i) {
          setTextVisible(false);
          setTimeout(() => {
            setClipIdx(i);
            setTextVisible(true);
          }, 200);
        }
        break;
      }
      offset += CLIPS[i].duration;
    }
    if (elapsed >= TOTAL_DURATION) {
      clearInterval(timerRef.current);
      setEnded(true);
    }
  }, [elapsed, open, clipIdx]);

  // Sync video element when clip changes
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !open) return;
    v.load();
    v.play().catch(() => {});
  }, [clipIdx, open]);

  // Mute/unmute
  useEffect(() => {
    if (narrationRef.current) narrationRef.current.muted = muted;
    if (musicRef.current) musicRef.current.muted = muted;
  }, [muted]);

  const handleClose = useCallback(() => {
    setOpen(false);
    narrationRef.current?.pause();
    musicRef.current?.pause();
    if (narrationRef.current) narrationRef.current.currentTime = 0;
    if (musicRef.current) musicRef.current.currentTime = 0;
    clearInterval(timerRef.current);
  }, []);

  const handleWhatsApp = () => {
    handleClose();
    window.open(getWhatsAppLink("Hola NaviCargo, vi su video y me gustaría recibir información sobre sus servicios logísticos."), "_blank", "noopener,noreferrer");
  };

  if (!open) return null;

  const progress = Math.min((elapsed / TOTAL_DURATION) * 100, 100);
   const clip = isEnglish ? { ...CLIPS[clipIdx], ...EN_CLIPS[clipIdx] } : CLIPS[clipIdx];

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
       aria-label={isEnglish ? "NaviCargo promotional video" : "Video promocional NaviCargo"}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/80"
        onClick={handleClose}
      />

      {/* Hidden audio */}
      <audio ref={narrationRef} src={isEnglish ? "/promo/navicargo-narration-en.mp3" : "/promo/navicargo-narration.mp3"} preload="auto" />
      <audio ref={musicRef} src="/promo/navicargo-bg-music.mp3" preload="auto" loop />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-3xl rounded-2xl overflow-hidden border border-zinc-800/80 bg-black">

        {/* Progress bar */}
        <div className="absolute top-0 left-0 right-0 z-30 h-[3px] bg-zinc-800">
          <div
            className="h-full bg-[#22C55E] transition-none"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Top controls */}
        <div className="absolute top-4 left-4 right-4 z-30 flex items-center justify-end">
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                if (!audioReady) {
                  void startAudio();
                  return;
                }
                setMuted((value) => !value);
              }}
              className={`w-8 h-8 rounded-full bg-black/50 flex items-center justify-center transition-colors ${
                audioNeedsGesture && !muted ? "text-[#22C55E]" : "text-white/70 hover:text-white"
              }`}
               aria-label={!audioReady ? t("promo.enableSound", "Activar sonido") : muted ? t("promo.unmute", "Activar sonido") : t("promo.mute", "Silenciar")}
              aria-pressed={muted}
            >
              {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
            <button
              onClick={handleClose}
              className="w-8 h-8 rounded-full bg-black/50 flex items-center justify-center text-white/70 hover:text-white transition-colors"
              aria-label="Cerrar video"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Video layer */}
        {!ended && (
          <div className="relative aspect-video bg-zinc-950">
            <video
              ref={videoRef}
              key={clip.src}
              className="w-full h-full object-cover"
              autoPlay
              muted
              playsInline
              loop
            >
              <source src={clip.src} type="video/mp4" />
            </video>

            {/* Dark gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-transparent" />

            {/* Text overlay */}
            <div
              className="absolute bottom-10 left-8 right-8"
              style={{
                opacity: textVisible ? 1 : 0,
                transform: textVisible ? "translateY(0)" : "translateY(10px)",
                transition: "opacity 0.4s ease, transform 0.4s ease",
              }}
            >
              <p className="text-white text-2xl md:text-3xl font-bold leading-tight tracking-tight whitespace-pre-line drop-shadow-2xl mb-2">
                {clip.headline}
              </p>
              {clip.sub && (
                <p className="text-zinc-300 text-sm md:text-base font-medium drop-shadow-md">
                  {clip.sub}
                </p>
              )}
            </div>

            {/* Clip counter dots */}
            <div className="absolute bottom-4 right-6 flex items-center gap-1.5">
              {CLIPS.map((_, i) => (
                <span
                  key={i}
                  className="rounded-full transition-all duration-300"
                  style={{
                    width: i === clipIdx ? "20px" : "6px",
                    height: "6px",
                    background: i === clipIdx ? "#22C55E" : "rgba(255,255,255,0.3)",
                  }}
                />
              ))}
            </div>
          </div>
        )}

        {/* End screen — CTA */}
        {ended && (
          <div className="relative aspect-video bg-zinc-950 flex flex-col items-center justify-center text-center px-8">
            <div className="absolute inset-0 bg-gradient-to-br from-[#22C55E]/10 via-zinc-950 to-zinc-950" />
            <div className="relative z-10 flex flex-col items-center gap-6">
              <img
                src="/navicargo-logo-white-transparent.png"
                alt="NaviCargo Logistics"
                className="h-10 w-auto"
              />
              <div>
                <p className="text-white text-2xl md:text-3xl font-bold tracking-tight mb-2">
                   {isEnglish ? "Your next operation starts here." : "Tu próxima operación empieza aquí."}
                </p>
                <p className="text-zinc-400 text-sm">
                   {isEnglish ? "Write to us and get immediate guidance from our logistics team." : "Escríbenos y recibe orientación inmediata de nuestro equipo logístico."}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row items-center gap-3">
                <button
                  onClick={handleWhatsApp}
                  className="inline-flex items-center gap-2 bg-[#22C55E] text-black font-bold px-6 py-3 rounded-full hover:bg-[#22C55E]/90 transition-colors"
                >
                  <MessageCircle className="w-4 h-4" />
                   {t("nav.quote", "Cotizar por WhatsApp")}
                </button>
                <Link
                  to="/services"
                  onClick={handleClose}
                  className="inline-flex items-center gap-2 text-zinc-300 hover:text-white text-sm font-medium transition-colors"
                >
                   {t("home.viewAll", "Ver todos los servicios")}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Bottom strip */}
        <div className="bg-zinc-950 border-t border-zinc-800/60 px-6 py-3 flex items-center justify-between gap-4">
          <p className="text-zinc-500 text-xs">
             {ended ? `NaviCargo Logistics — ${isEnglish ? "Global logistics · MX" : "Logística global · MX"}` : `${Math.ceil(TOTAL_DURATION - elapsed)}${isEnglish ? "s remaining" : "s restantes"}`}
          </p>
          <button
            onClick={handleClose}
            className="text-zinc-500 hover:text-zinc-300 text-xs transition-colors"
          >
             {ended ? (isEnglish ? "Close" : "Cerrar") : (isEnglish ? "Skip video" : "Saltar video")}
          </button>
        </div>
      </div>
    </div>
  );
}
